import React, { useState } from 'react';
import { Mail, Check } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useGroups } from '../store/groups';
import { useOrganizations } from '../store/organizations';
import { BottomSheet, Field, Input, PrimaryBtn, SecondaryBtn, ErrorMsg } from './UI';

export const InviteSheet: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { profile } = useAuth();
  const { currentGroup, sendInviteEmail } = useGroups();
  const { groupOrgInfo, myAdminOrgIds } = useOrganizations();
  const group = currentGroup();
  const groupOrg = group ? groupOrgInfo[group.id] : null;
  // Grupper som tilhører en organisasjon: kun org-admin (styret) kan invitere —
  // samme regel som i ProfilePage sitt gruppekort, se CLAUDE.md.
  const canInvite = groupOrg ? myAdminOrgIds.has(groupOrg.id) : true;

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const handleSend = async () => {
    if (!inviteEmail.trim() || !group || !profile) return;
    setInviteSending(true); setInviteError('');
    const err = await sendInviteEmail(inviteEmail.trim(), profile.full_name, group.name, group.invite_code);
    if (err) { setInviteError(err); setInviteSending(false); return; }
    setInviteSent(true); setInviteEmail('');
    setTimeout(() => { setInviteSent(false); onClose(); }, 2000);
    setInviteSending(false);
  };

  if (!group) return null;

  if (!canInvite) {
    return (
      <BottomSheet title="Inviter til gruppe" onClose={onClose}>
        <div style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.6, padding: '4px 0 16px' }}>
          Kun styret i {groupOrg!.name} kan invitere til «{group.name}».
        </div>
        <SecondaryBtn onClick={onClose}>Lukk</SecondaryBtn>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet title="Inviter til gruppe" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Invitasjonskode for «{group.name}»</div>
        <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 3, textAlign: 'center' }}>
          {group.invite_code}
        </div>
      </div>
      {inviteError && <ErrorMsg msg={inviteError} />}
      {inviteSent && (
        <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={14} strokeWidth={2.5} /> Invitasjon sendt!
        </div>
      )}
      <Field label="E-postadresse til mottaker">
        <Input type="email" placeholder="ola@eksempel.no" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} autoFocus />
      </Field>
      <PrimaryBtn onClick={handleSend} disabled={inviteSending}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {inviteSending ? 'Sender...' : <><Mail size={15} strokeWidth={2} /> Send invitasjon</>}
        </span>
      </PrimaryBtn>
      <SecondaryBtn onClick={onClose}>Avbryt</SecondaryBtn>
    </BottomSheet>
  );
};
